/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 22:37:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, getFormValues, reset, change } from 'redux-form';
import { Modal, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-custom';

import { validateSpecificCollectionsItemBeforeSave } from 'src/helpers/custom-sorting-helper';

type Props = {
  active: boolean,
  customSorting: Object,
  handleChange: () => void,
  updateSpecificCollectionsItem: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  collectionOptions: Array<{ value: string, label: string }>,
  sortOptions: Array<{ value: string, label: string }>
};

class SpecificCollectionsModal extends Component<ComposedProps, State> {
  state = {
    collectionOptions: [],
    sortOptions: []
  };

  componentWillReceiveProps(nextProps) {
    const { active } = this.props;
    const { active: nextActive, index } = nextProps;
    if (!active && nextActive) {
      // Calculator new collection options and sort options
      const collectionOptions = this.buildCollectionOptions(index);
      const sortOptions = this.buildSortOptions(index);
      this.setState({ collectionOptions, sortOptions });
    }
  }
  render() {
    const { i18n, active, handleSubmit, pristine, specificCollectionsItem, collectionValue } =
      this.props;
    const title = _.isEmpty(specificCollectionsItem)
      ? i18n.translate('AdminHTML.CustomSorting.Edit.title_AddSorting')
      : i18n.translate('AdminHTML.CustomSorting.Edit.title_EditSorting');

    const { collectionOptions, sortOptions } = this.state;

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled: pristine
        }}>
        <Modal.Section>
          <div className="BC-Modal__CustomSorting">
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="sortId"
                  component={RenderSelect}
                  label={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.sortingOption'
                  )}
                  placeholder={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.sortingOption_Placeholder'
                  )}
                  options={sortOptions}
                />
                <Field
                  name="collectionIds"
                  component={RenderAutocomplete}
                  label={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.collections'
                  )}
                  placeholder={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.collections_Placeholder'
                  )}
                  options={collectionOptions}
                  value={collectionValue}
                  onChange={this.handleChangeCollections}
                />
              </FormLayout>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  buildCollectionOptions = (index: number) => {
    const { collections: collectionOptions = [], specificCollections } = this.props;
    let result = JSON.parse(JSON.stringify(collectionOptions));
    // remove exist collection
    _.map(specificCollections, (item, key) => {
      if (key !== index) {
        const { collectionIds = [] } = item;
        _.map(collectionIds, collection => {
          _.remove(result, item => item.value === collection.value);
        });
      }
    });
    return result;
  };

  buildSortOptions = (index: number) => {
    const { optionSorts = [], specificCollections } = this.props;
    let result = JSON.parse(JSON.stringify(optionSorts));

    // remove exist sort ID
    _.map(specificCollections, (item, key) => {
      if (key !== index) {
        const { sortId = '' } = item;
        _.remove(result, item => item.value === sortId);
      }
    });
    return result;
  };

  handleChangeCollections = collectionIds => {
    this.props.updateField('collectionIds', collectionIds);
  };

  handleChange = () => {
    this.props.resetForm();
    this.props.handleChange();
  };

  handleSave = data => {
    const { index, updateSpecificCollectionsItem, i18n } = this.props;

    if (validateSpecificCollectionsItemBeforeSave(data, i18n)) {
      updateSpecificCollectionsItem(data, index);
    }
    this.handleChange();
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }
}

const SpecificCollectionsModalForm = reduxForm({
  form: 'SpecificCollectionsModalForm', // a unique identifier for this form
  enableReinitialize: true
})(SpecificCollectionsModal);

const mapStateToProps = (state, props) => {
  const { specificCollectionsItem } = props;
  const formValues = getFormValues('SpecificCollectionsModalForm')(state);
  const collectionValue = _.get(formValues, 'collections', []);
  return {
    collections: _.get(state.filterData.filterData, 'collections', []),
    collectionValue,
    initialValues: specificCollectionsItem
  };
};

const mapDispatchToProps = dispatch => ({
  // Reset form
  resetForm: () => dispatch(reset('SpecificCollectionsModalForm')),
  updateField: (field, value) => dispatch(change(`SpecificCollectionsModalForm`, field, value))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withI18n()(SpecificCollectionsModalForm));
